"use client";

import { useEffect, useRef, useState } from "react";

const stages = ["제품 캠페인", "카페 브랜드", "건축·공간"];

export function AuthShowcase() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [enabled, setEnabled] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const [stage, setStage] = useState(0);
  const [muted, setMuted] = useState(true);
  const [volume, setVolume] = useState(0.5);
  const lastVolume = useRef(0.5);
  const soundPreference = useRef({ muted: true, volume: 0.5 });

  function changeVolume(value: number) {
    const video = videoRef.current;
    if (!video) return;
    video.volume = value;
    video.muted = value === 0;
    soundPreference.current = { muted: video.muted, volume: value };
    if (value > 0) lastVolume.current = value;
    setVolume(value);
    setMuted(video.muted);
  }

  function toggleMute() {
    const video = videoRef.current;
    if (!video) return;
    if (video.muted || video.volume === 0) {
      changeVolume(video.volume > 0 ? video.volume : lastVolume.current);
    } else {
      video.muted = true;
      soundPreference.current.muted = true;
      setMuted(true);
    }
  }

  useEffect(() => {
    if (videoRef.current) videoRef.current.volume = 0.5;
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => {
      if (preference.matches) videoRef.current?.pause();
      setEnabled(!preference.matches);
    };
    sync();
    preference.addEventListener("change", sync);
    return () => preference.removeEventListener("change", sync);
  }, []);

  async function togglePlayback() {
    const video = videoRef.current;
    if (!video) return;
    if (!video.paused) {
      video.pause();
      return;
    }
    if (!enabled) {
      video.src = "/content/dream-studio-workflow-v4.mp4";
      setEnabled(true);
    }
    try {
      video.volume = soundPreference.current.volume;
      video.muted = soundPreference.current.muted;
      await video.play();
    } catch {
      setPlaying(false);
    }
  }

  return (
    <figure className="ws-auth-showcase">
      <div className="ws-auth-showcase-heading">
        <span>세 가지 콘셉트, 무한한 가능성</span>
        <span className="ws-auth-example">30초 제작 예시</span>
      </div>
      <div className="ws-auth-film">
        <video
          ref={videoRef}
          src={enabled && !failed ? "/content/dream-studio-workflow-v4.mp4" : undefined}
          poster="/content/dream-studio-workflow-poster-v4.jpg"
          autoPlay={enabled && !failed}
          muted={muted}
          loop
          playsInline
          preload="none"
          width={1280}
          height={720}
          aria-label="제품 캠페인, 카페 브랜드, 건축·공간 이미지와 영상으로 소개하는 dream studio 제작 예시"
          aria-describedby="auth-showcase-description"
          onLoadedMetadata={(event) => {
            event.currentTarget.volume = soundPreference.current.volume;
            event.currentTarget.muted = soundPreference.current.muted;
          }}
          onVolumeChange={(event) => {
            setMuted(event.currentTarget.muted);
            setVolume(event.currentTarget.volume);
          }}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onError={() => { setFailed(true); setPlaying(false); }}
          onTimeUpdate={(event) => {
            const time = event.currentTarget.currentTime;
            setStage(time < 10 ? 0 : time < 20 ? 1 : 2);
          }}
        />
      </div>
      {!failed && <div className="ws-auth-controls" role="group" aria-label="소개 영상 컨트롤">
        <button
          type="button"
          className="ws-auth-playback"
          onClick={() => void togglePlayback()}
          aria-label={playing ? "소개 영상 일시정지" : "소개 영상 재생"}
        >
          <span aria-hidden="true">{playing ? "Ⅱ" : "▶"}</span>
          {playing ? "일시정지" : "재생"}
        </button>
        <div className="ws-auth-volume">
          <button type="button" className="ws-auth-sound" onClick={toggleMute}
            aria-label={muted || volume === 0 ? "소개 영상 소리 켜기" : "소개 영상 음소거"}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
              <path d="M11 5 6 9H3v6h3l5 4V5Z" strokeLinejoin="round" />
              {muted || volume === 0 ? <path d="m16 9 6 6m0-6-6 6" /> : <><path d="M15 8a6 6 0 0 1 0 8" /><path d="M18 5a10 10 0 0 1 0 14" /></>}
            </svg>
            <span>{muted || volume === 0 ? "소리 켜기" : "음소거"}</span>
          </button>
          <input type="range" min="0" max="100" step="5"
            aria-label="소개 영상 볼륨" aria-valuetext={`${muted ? 0 : Math.round(volume * 100)}%`}
            value={muted ? 0 : Math.round(volume * 100)}
            onChange={(event) => changeVolume(Number(event.currentTarget.value) / 100)} />
          <span className="ws-auth-volume-value" aria-hidden="true">{muted ? 0 : Math.round(volume * 100)}%</span>
        </div>
      </div>}
      <figcaption id="auth-showcase-description">
        <ol className="ws-auth-stages" aria-label="콘텐츠 제작 예시">
          {stages.map((label, index) => <li key={label} data-active={stage === index && enabled && !failed}>{label}</li>)}
        </ol>
        <p>프롬프트로 만들고, 결과를 모아보고, 마음에 드는 장면을 다운로드하세요.</p>
      </figcaption>
    </figure>
  );
}
